/**
 * 등하원 위치 공유 — 명세서 §5.10 (v1.22)
 *
 * 보호자가 **동의하고 직접 켠 동안만** 대략적인 위치를 담임·원장에게 보여 준다.
 * 좌표는 저장 전에 소수점 셋째 자리(약 100m)로 반올림해 "대략적 위치"만 남기고,
 * 일정 시간이 지나면 자동으로 끝나며 당일이 지나면 지운다.
 * 서버(반올림·거리 계산)와 화면(문구·간격)이 이 파일 하나를 본다.
 */
export const PICKUP_TRIP_KINDS = {
    /** 등원 — 원으로 오는 길 */
    DROP_OFF: 'DROP_OFF',
    /** 하원 — 데리러 오는 길 */
    PICK_UP: 'PICK_UP',
};
export const PICKUP_TRIP_KIND_LABELS = {
    DROP_OFF: '등원 중',
    PICK_UP: '하원 데리러 가는 중',
};
/** 소수점 셋째 자리 = 약 110m — 이보다 정확한 위치는 저장하지 않는다 */
export const LOCATION_ROUND_DECIMALS = 3;
/** 켜 둔 채 잊어도 이 시간이 지나면 서버가 공유를 끝낸다 */
export const PICKUP_TRIP_AUTO_END_MINUTES = 120;
/** 끝난 공유 기록은 이 시간이 지나면 지운다 — 이동 이력을 남기지 않는다 */
export const PICKUP_TRIP_RETENTION_HOURS = 24;
/** 화면이 위치를 다시 보내는 최소 간격 */
export const PICKUP_TRIP_UPDATE_INTERVAL_MS = 20_000;
/** 대략적 위치로 반올림 — 위·경도 공통 */
export function roundCoordinate(value) {
    const factor = 10 ** LOCATION_ROUND_DECIMALS;
    return Math.round(value * factor) / factor;
}
/** 두 지점의 대략 거리(m) — 하버사인. 원까지 얼마나 남았는지 안내에만 쓴다 */
export function haversineMeters(a, b) {
    const R = 6_371_000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return Math.round(2 * R * Math.asin(Math.sqrt(s)));
}
/** "약 850m" / "약 1.2km" — 반올림한 좌표라 언제나 '약'을 붙인다 */
export function formatDistance(meters) {
    if (meters < 1000)
        return `약 ${Math.max(100, Math.round(meters / 50) * 50)}m`;
    return `약 ${(meters / 1000).toFixed(1)}km`;
}
/** 카카오맵 링크 — 앱이 없으면 브라우저 지도로 열린다 */
export function mapLink(point, label) {
    return `https://map.kakao.com/link/map/${encodeURIComponent(label)},${point.lat},${point.lng}`;
}
/** 학부모·교직원 노출 문구 (§13.1-③) */
export const PICKUP_LOCATION_COPY = {
    title: '등하원 위치 공유',
    intro: '출발할 때 켜 두면 담임 선생님과 원장님이 대략적인 위치(약 100m 단위)만 볼 수 있어요. 도착하거나 끄면 바로 멈추고, 기록은 그날이 지나면 지워져요.',
    consentLabel: '위치 공유에 동의해요',
    consentHint: '동의는 언제든 끌 수 있어요. 켜 두어도 "출발했어요"를 누른 동안에만 위치가 전해져요.',
    startDropOff: '등원 출발했어요',
    startPickUp: '하원 데리러 가요',
    sharing: (kindLabel) => `${kindLabel} · 위치를 공유하고 있어요`,
    keepOpenHint: '앱을 열어 둔 동안 위치가 갱신돼요. 화면을 닫아도 마지막 위치는 잠시 남아요.',
    stop: '도착했어요 · 공유 끝내기',
    lastSeen: (relative) => `마지막 위치 ${relative}`,
    distanceToFacility: (distance) => `원까지 ${distance}`,
    needConsent: '위치 공유에 먼저 동의해 주세요.',
    permissionDenied: '위치 권한이 필요해요. 기기 설정에서 위치를 허용해 주세요.',
    staffTitle: '지금 오는 중',
    staffEmpty: '지금 위치를 공유 중인 가정이 없어요.',
    staffHint: '보호자가 동의하고 직접 켠 동안만 보여요. 약 100m 단위의 대략적 위치예요.',
    departedTitle: '보호자가 출발했어요',
    departedBody: (childName, kindLabel) => `${childName} 보호자가 ${kindLabel}이에요.`,
    mapLabel: '지도 보기',
};
//# sourceMappingURL=pickup-location.js.map